import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { httpErrorMessage } from '../http/http-error-message';
import { ApiError } from '../models/api.model';
import { SKIP_ERROR_TOAST } from '../interceptors/error-toast.token';
import { SKIP_HTTP_DELAY, SKIP_LOADING } from '../interceptors/loading.interceptor';

// ── Shared resource shape used by the callback-based API ──────────────────

export interface Resource {
  url: string;
  method: string;
  body?: any;
  headers?: HttpHeaders | Record<string, string>;
  /** Skip global spinner + artificial delay (lookups, background prefetch). */
  skipLoading?: boolean;
  /** Fail quietly: no error toast (session expiry is still handled). */
  skipErrorToast?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// RestService
//
// Two complementary HTTP APIs in one service:
//
//   1. Observable API  — thin HttpClient wrapper used by every feature service.
//      Paths are automatically prefixed with environment.apiBaseUrl.
//      Errors are normalised to ApiError.
//
//   2. Callback API    — legacy callServer / propertyCallServer interface that
//      accepts a pre-built Resource object (from ConfigService.getRestObject)
//      and delivers results via success/error callbacks (cbs / cbe).
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class RestService {
  // Needed for the dynamic method dispatch in callServer.
  [key: string]: any;

  constructor(private readonly http: HttpClient) {}

  // ── Observable API ────────────────────────────────────────────────────────

  get<T>(path: string, params?: Record<string, string | number | boolean>): Observable<T> {
    return this.http
      .get<T>(this.baseUrl(path), { params: this.toHttpParams(params) })
      .pipe(catchError(this.normalizeError));
  }

  post<T, B = unknown>(path: string, body: B): Observable<T> {
    return this.http
      .post<T>(this.baseUrl(path), body)
      .pipe(catchError(this.normalizeError));
  }

  put<T, B = unknown>(path: string, body: B): Observable<T> {
    return this.http
      .put<T>(this.baseUrl(path), body)
      .pipe(catchError(this.normalizeError));
  }

  patch<T, B = unknown>(path: string, body: B): Observable<T> {
    return this.http
      .patch<T>(this.baseUrl(path), body)
      .pipe(catchError(this.normalizeError));
  }

  delete<T>(path: string): Observable<T> {
    return this.http
      .delete<T>(this.baseUrl(path))
      .pipe(catchError(this.normalizeError));
  }

  // ── Callback API ──────────────────────────────────────────────────────────

  /**
   * Execute a REST call described by a `Resource` object.
   * Dispatches to the appropriate `*Call` method based on `resource.method`.
   *
   * @param resource  `{ url, method, body?, headers? }` — build via `ConfigService.getRestObject`
   * @param cbs       Success callback `(response) => void`
   * @param cbe       Error callback   `(error)    => void`
   * @deprecated Prefer {@link execute} + Observable subscribe / async pipe.
   */
  callServer(resource: Resource, cbs: any, cbe: any): void {
    this.execute(resource).subscribe(
      (response: any) => cbs(response),
      (error: any)    => cbe(this.handleError(error)),
    );
  }

  /**
   * Observable-first Resource execution — preferred over {@link callServer}.
   * Default type is `any` for gradual migration; pass an explicit type param when known.
   */
  execute<T = any>(resource: Resource): Observable<T> {
    const method = String(resource.method ?? 'GET').trim().toLowerCase();
    switch (method) {
      case 'get':
        return this.getCall(resource) as Observable<T>;
      case 'post':
        return this.postCall(resource) as Observable<T>;
      case 'put':
        return this.putCall(resource) as Observable<T>;
      case 'patch':
        return this.patchCall(resource) as Observable<T>;
      case 'delete':
        return this.deleteCall(resource) as Observable<T>;
      default:
        return throwError(() => new Error(`Unsupported HTTP method: ${resource.method}`));
    }
  }

  /**
   * POST variant that sends custom per-call headers (e.g. for file uploads).
   */
  propertyCallServer(resource: Resource, cbs: any, cbe: any): void {
    const headers = new HttpHeaders();
    this.http.post(resource['url'], resource['body'], { headers }).subscribe(
      (response: any) => cbs(response),
      (error: any)    => cbe(this.handleError(error)),
    );
  }

  // ── Low-level call methods (used by callServer dispatch) ──────────────────

  getCall(resource: Resource): Observable<any> {
    return this.http.get(resource.url, {
      headers: resource.headers ?? new HttpHeaders(),
      context: this.resourceContext(resource),
    });
  }

  postCall(resource: Resource): Observable<any> {
    return this.http.post(resource.url, resource.body, {
      headers: resource.headers ?? new HttpHeaders(),
      context: this.resourceContext(resource),
    });
  }

  putCall(resource: Resource): Observable<any> {
    return this.http.put(resource.url, resource.body, {
      headers: resource.headers ?? new HttpHeaders(),
      context: this.resourceContext(resource),
    });
  }

  patchCall(resource: Resource): Observable<any> {
    return this.http.patch(resource.url, resource.body, {
      headers: resource.headers ?? new HttpHeaders(),
      context: this.resourceContext(resource),
    });
  }

  deleteCall(resource: Resource): Observable<any> {
    if (resource['body'] != null) {
      return this.http.delete(resource['url'], { body: resource['body'] });
    }
    return this.http.delete(resource['url']);
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  downloadExistingPdf(pdfUrl: string): Observable<Blob> {
    return this.http.get(pdfUrl, { responseType: 'blob' });
  }

  /**
   * GET a binary resource and return the full HTTP response so callers can
   * read `Content-Disposition` / `Content-Type` headers (e.g. job-file download).
   */
  downloadBlobResponse(url: string): Observable<HttpResponse<Blob>> {
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  /**
   * Legacy error handler for the callback API.
   * Reloads the page on a chunk-load failure; otherwise returns the error
   * so the `cbe` callback receives it.
   */
  handleError(error: any): any {
    const chunkFailed = /Loading chunk [\d]+ failed/;
    if (chunkFailed.test(error?.message ?? '')) {
      window.location.reload();
      return;
    }
    return error;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private resourceContext(resource: Resource): HttpContext {
    let context = new HttpContext();
    if (resource.skipLoading) {
      context = context.set(SKIP_LOADING, true).set(SKIP_HTTP_DELAY, true);
    }
    if (resource.skipErrorToast) {
      context = context.set(SKIP_ERROR_TOAST, true);
    }
    return context;
  }

  /** Prefix relative paths with apiBaseUrl; leave absolute URLs untouched. */
  private baseUrl(path: string): string {
    return path.startsWith('http') ? path : `${environment.apiBaseUrl}${path}`;
  }

  private toHttpParams(params?: Record<string, string | number | boolean>): HttpParams {
    let p = new HttpParams();
    if (!params) return p;
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) p = p.set(k, String(v));
    }
    return p;
  }

  /**
   * Observable-chain error normaliser — converts any thrown value into a
   * typed ApiError so all feature services get a consistent error shape.
   * Handles HttpErrorResponse, plain strings, and arbitrary objects.
   */
  private readonly normalizeError = (err: unknown): Observable<never> => {
    if (err instanceof HttpErrorResponse) {
      return throwError(() =>
        this.toApiError(err.status, httpErrorMessage(err.status, err.error, err.statusText), err.error),
      );
    }
    // Plain string re-thrown by interceptors or services
    if (typeof err === 'string') {
      return throwError(() =>
        this.toApiError(0, err || 'An unexpected error occurred', err),
      );
    }
    // ApiError or arbitrary object
    const e = err as Record<string, unknown>;
    const message = typeof e?.['message'] === 'string' ? e['message'] : 'An unexpected error occurred';
    return throwError(() => this.toApiError(e?.['status'], message, err));
  };

  /** Coerces unknown status values to a finite HTTP-style code (0 = non-HTTP / client). */
  private toApiError(status: unknown, message: string, details?: unknown): ApiError {
    const code =
      typeof status === 'number' && Number.isFinite(status) ? status : 0;
    return { status: code, message, details };
  }
}
